import React, { useState } from "react";
import { 
  Box, TextField, Button, Typography, Paper, AppBar, Toolbar, Container, CssBaseline, Stack 
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { QRCodeCanvas } from "qrcode.react";
import DOMPurify from "dompurify";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";

/* ✅ กำหนดธีมสีม่วง */
const theme = createTheme({
  typography: {
    fontFamily: "Mitr, sans-serif", // ✅ ใช้ฟอนต์ Mitr
    h4: { fontWeight: 300 }, // ✅ ลดหัวข้อให้เบาขึ้น
    h6: { fontWeight: 400 }, // ✅ ลดฟอนต์ Header
    body1: { fontWeight: 300 }, // ✅ ลดฟอนต์เนื้อหาทั่วไป
  },
  palette: {
    primary: { main: "#652D91" }, 
    secondary: { main: "#AB47BC" }, 
    error: { main: "#D32F2F" }, 
  },
});

const QRCodeGenerator = () => {
  const [url, setUrl] = useState("");
  const [validUrl, setValidUrl] = useState("");

  const handleGenerate = () => {
    const sanitizedUrl = DOMPurify.sanitize(url);
    
    if (!sanitizedUrl || sanitizedUrl.startsWith("about:")) {
      alert("URL ไม่ถูกต้อง กรุณาตรวจสอบ!");
      return;
    }

    setValidUrl(sanitizedUrl);
  };

  const handleDownload = () => {
    const canvas = document.querySelector("canvas");
    const pngUrl = canvas.toDataURL("image/png");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = "qrcode.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleReset = () => {
    setUrl("");
    setValidUrl("");
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Header */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            QR Code Generator
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="sm">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="80vh">
          <Paper 
            elevation={6} 
            sx={{ 
              padding: 5, 
              borderRadius: 4, 
              textAlign: "center", 
              width: "100%", 
              maxWidth: "550px",  
              background: "radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, rgba(230, 230, 230, 0.12) 100%)", // ✅ Transparent Smoke Effect
              backdropFilter: "blur(8px)", // ✅ เพิ่มเบลอเพื่อให้พื้นหลังดูนุ่มขึ้น
              boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)", // ✅ เพิ่มเงาให้ดูมีมิติ
            }}
          >
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ fontWeight: "bold", color: "primary.main" }}  
            >
              สร้างรหัส QR Code
            </Typography>

            <TextField
              fullWidth
              label="ใส่ลิงค์ของคุณที่นี่"
              variant="outlined"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              sx={{ marginBottom: 3 }}
            />

            <Button 
              variant="contained" 
              color="primary" 
              fullWidth 
              onClick={handleGenerate}
              sx={{ 
                padding: 2, 
                fontSize: "1.2rem", 
                borderRadius: "20px"  // ✅ ทำให้ปุ่มโค้งมน
              }}  
            >
              Generate
            </Button>

            {validUrl && (
              <Box mt={4} display="flex" flexDirection="column" alignItems="center">
                <Box 
                  sx={{ 
                    backgroundColor: "#ffffff", 
                    padding: 4, 
                    borderRadius: 3, 
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)" 
                  }}
                >
                  <QRCodeCanvas value={validUrl} size={200} />  
                </Box>

                {/* ปุ่มดาวน์โหลด & รีเซ็ต QR */}
                <Stack direction="row" spacing={2} sx={{ marginTop: 3, width: "100%", justifyContent: "space-between" }}>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    startIcon={<RefreshIcon />}
                    sx={{ padding: 1.5, fontSize: "1rem", borderRadius: 3, flex: 1 }}
                    onClick={handleReset}
                  >
                    สร้าง QR ใหม่
                  </Button>

                  <Button 
                    variant="contained" 
                    color="secondary" 
                    startIcon={<DownloadIcon />}
                    sx={{ padding: 2, fontSize: "1.2rem", borderRadius: 3, flex: 1.5 }}
                    onClick={handleDownload}
                  >
                    ดาวน์โหลด
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>

      {/* Footer */}
      <Box component="footer" sx={{ textAlign: "center", padding: 2, backgroundColor: "#EDE7F6", marginTop: "auto" }}>
        <Typography variant="body2" color="textSecondary">
          &copy; {new Date().getFullYear()} ระบบสร้างรหัสคิวอาร์โค้ด | มหาวิทยาลัยเทคโนโลยีราชมงคลพระนคร
        </Typography>
      </Box>
    </ThemeProvider>
  );
};

export default QRCodeGenerator;